import React from 'react';
import { generateAgentScript } from './agentScriptsApi';

const DUPLICATE_NAME_PATTERN = /already exists/i;
const FALLBACK_ERROR = "Couldn't generate a valid script, try rephrasing your description.";

/**
 * Owns the lifecycle of a "generate agent script" request: validation,
 * in-flight state, and the two error channels the form renders.
 *
 * - `nameError` is surfaced inline on the name field (empty / duplicate name).
 * - `error` is the general form-level error.
 *
 * `generate` trims its inputs, validates them, calls the API, and returns the
 * saved record on success or `null` (with an error set) on failure.
 */
export const useGenerateAgentScript = () => {
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [nameError, setNameError] = React.useState(null);

  const clearErrors = React.useCallback(() => {
    setError(null);
    setNameError(null);
  }, []);

  const generate = React.useCallback(
    async ({ name, description }) => {
      const trimmedName = name.trim();
      const trimmedDescription = description.trim();

      clearErrors();
      if (!trimmedName) {
        setNameError('Enter a name for the agent script.');
        return null;
      }
      if (!trimmedDescription) {
        setError('Enter a description of what the agent should do.');
        return null;
      }

      setIsGenerating(true);
      const { status, data } = await generateAgentScript({ name: trimmedName, description: trimmedDescription });
      setIsGenerating(false);

      if (status === 200) {
        return data;
      }

      const message = data?.error || FALLBACK_ERROR;
      if (status === 400 && DUPLICATE_NAME_PATTERN.test(message)) {
        setNameError(message);
      } else {
        setError(message);
      }
      return null;
    },
    [clearErrors]
  );

  return { generate, isGenerating, error, nameError, clearErrors };
};
